
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { LoginForm } from "@/components/auth/LoginForm";
import { SignUpForm } from "@/components/auth/SignUpForm";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";
import { ResetPasswordForm } from "@/components/auth/ResetPasswordForm";
import { motion } from "framer-motion";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";

type AuthMode = "login" | "signup" | "forgot-password" | "reset-password";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<AuthMode>(() => {
    // First check if there's a reset token in the URL
    // Check multiple possible ways that Supabase might include the token
    const accessTokenInHash = location.hash.includes("access_token");
    const accessTokenInQuery = searchParams.get('access_token');
    const recoveryType = searchParams.get('type') === 'recovery';
    const hasResetToken = accessTokenInHash || accessTokenInQuery || recoveryType;
    
    if (hasResetToken) {
      console.log("Reset token detected in URL, setting mode to reset-password");
      return "reset-password";
    }
    
    // Then check mode parameter
    const modeParam = searchParams.get("mode");
    if (modeParam === "signup") return "signup";
    if (modeParam === "forgot-password") return "forgot-password";
    if (modeParam === "reset-password") return "reset-password";
    return "login";
  });

  // Update mode whenever URL parameters change
  useEffect(() => {
    // Check for reset token (can be in hash fragment or query parameters)
    const accessTokenInHash = location.hash.includes("access_token");
    const accessTokenInQuery = searchParams.get('access_token');
    const recoveryType = searchParams.get('type') === 'recovery';
    const hasResetToken = accessTokenInHash || accessTokenInQuery || recoveryType;
    
    if (hasResetToken) {
      console.log("Reset token detected in URL from useEffect, setting mode to reset-password");
      setMode("reset-password");
      return;
    }
    
    const modeParam = searchParams.get("mode");
    if (modeParam === "signup") {
      setMode("signup");
    } else if (modeParam === "forgot-password") {
      setMode("forgot-password");
    } else if (modeParam === "reset-password") {
      setMode("reset-password");
    } else if (!modeParam) {
      setMode("login");
    }
  }, [searchParams, location.hash, location]);

  // Handle mode changes by updating URL
  const handleModeChange = (newMode: AuthMode) => {
    if (newMode === "login") {
      navigate("/auth");
    } else {
      navigate(`/auth?mode=${newMode}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFE29F] via-[#FFA99F] to-[#FF719A] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex items-center gap-2"
      >
        <motion.div
          animate={{
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1, 1.1, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "loop"
          }}
        >
          <Sparkles className="h-8 w-8 text-white" />
        </motion.div>
        <h1 className="text-3xl font-bold text-white">
          MiniMakes
        </h1>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="p-6 shadow-xl bg-white/90 backdrop-blur-sm border-0">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {mode === "login" && (
              <LoginForm 
                onForgotPassword={() => handleModeChange("forgot-password")}
                onSignUpClick={() => handleModeChange("signup")}
              />
            )}
            {mode === "signup" && (
              <SignUpForm
                onLoginClick={() => handleModeChange("login")}
              />
            )}
            {mode === "forgot-password" && (
              <ForgotPasswordForm
                onBackToLogin={() => handleModeChange("login")}
              />
            )}
            {mode === "reset-password" && (
              <ResetPasswordForm />
            )}
          </motion.div>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-8 text-white/80 text-sm text-center max-w-md"
      >
        <p>Join our community of young chefs and start your cooking adventure today!</p>
      </motion.div>
    </div>
  );
};

export default Auth;
